import { View } from "@-/com.npmjs.react-native";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { GraphemeSvgGlyphPlayer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useMemo } from "react";

import { fetchSlideSvgGlyphStrokes } from "#fetch-slide-svg-glyph-strokes";

function coerceSize(size: number | string | undefined, fallback: number): number {
  if (size === undefined) {
    return fallback;
  }
  const parsed = typeof size === "number" ? size : Number(size);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function coerceBoolean(value: boolean | string | undefined, fallback: boolean): boolean {
  if (value === undefined) {
    return fallback;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return fallback;
}

/**
 * Animated grapheme SVG stroke-order demo for slide MDX.
 *
 * Registered as `SvgGlyph`, so authors write `<SvgGlyph glyph="丨" />` under the prose that
 * introduces the character. Loads stroke data from the static DB and plays
 * {@link GraphemeSvgGlyphPlayer} when the Learn card surface is active.
 */
export function SlideSvgGlyph({
  glyph,
  loop = true,
  showBackground = true,
  size = 180,
}: {
  glyph: string;
  loop?: boolean | string;
  showBackground?: boolean | string;
  size?: number | string;
}) {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const { frontVisibleSignal, isActive } = useLearnCardSurface();
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = useMemo(() => getGraphemeGlyph(glyph), [getGraphemeGlyph, glyph]);
  const resolvedSize = coerceSize(size, 180);
  const resolvedLoop = coerceBoolean(loop, true);
  const resolvedShowBackground = coerceBoolean(showBackground, true);

  const { data: strokeListByGlyph } = useQuery({
    queryFn: () => fetchSlideSvgGlyphStrokes(staticDb, [glyph]),
    queryKey: ["slide-svg-glyph-strokes", glyph],
  });

  // `undefined` while loading → font fallback footprint; missing/empty stroke data stays null so
  // the player keeps showing the font glyph instead of an empty canvas.
  const strokeList = useMemo(() => {
    if (strokeListByGlyph === undefined) {
      return null;
    }
    return strokeListByGlyph[glyph] ?? null;
  }, [glyph, strokeListByGlyph]);

  return (
    <View className="items-center justify-center self-center py-2">
      <GraphemeSvgGlyphPlayer
        autoPlay={isActive}
        glyph={graphemeGlyph}
        loop={resolvedLoop}
        playSignal={frontVisibleSignal}
        showBackground={resolvedShowBackground}
        showStrokeControls={false}
        size={resolvedSize}
        strokeList={strokeList}
      />
    </View>
  );
}
